// frontend/src/components/chat/AttachmentItem.jsx
// Render de un adjunto en un mensaje: imagen (preview + modal), nota de voz
// (reproductor) o documento (descarga). Todo via endpoint protegido (blob).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress, Dialog, IconButton, Stack, Typography, Tooltip,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon, Download as DownloadIcon, Close as CloseIcon,
  PictureAsPdf as PdfIcon, Description as DocIcon, TableChart as SheetIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';
import AudioPlayer from './AudioPlayer';

const fmtSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForMime = (mime = '', name = '') => {
  const n = (name || '').toLowerCase();
  if ((mime || '').includes('pdf') || n.endsWith('.pdf')) return PdfIcon;
  if (n.match(/\.(xls|xlsx|csv)$/)) return SheetIcon;
  if (n.match(/\.(doc|docx|txt)$/)) return DocIcon;
  return FileIcon;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'archivo';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// Estado de transcodificación en segundo plano del backend.
// Cuando es 'pending'/'processing' el medio aún NO está en su formato final
// compatible, así que no debemos intentar cargarlo todavía en el navegador.
const isTranscoding = (a) => {
  const st = a && a.transcode_status;
  return st === 'pending' || st === 'processing';
};

// Estado discreto "Procesando…" mostrado en lugar del player mientras el
// backend convierte el medio. Es puramente visual: NO dispara requests.
const ProcessingBubble = ({ kind }) => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row" spacing={1} alignItems="center"
      sx={{
        mt: 0.5, px: 1.25, py: 0.75, borderRadius: 1.5, border: '1px dashed',
        borderColor: 'divider', bgcolor: 'action.hover', maxWidth: 280,
      }}
    >
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        {kind === 'video' ? t('hardcoded.components_chat_attachmentitem.procesando_video') : t('hardcoded.components_chat_attachmentitem.procesando_audio')}
      </Typography>
    </Stack>
  );
};

const AttachmentItem = ({ attachment }) => {
  const isImage = (attachment.mime_type || '').startsWith('image/');
  const isAudio = attachment.is_voice || attachment.attachment_kind === 'voice'
    || (!attachment.attachment_kind && (attachment.mime_type || '').startsWith('audio/'));
  const isVideo = attachment.attachment_kind === 'video_note'
    || (attachment.attachment_kind !== 'voice' && (attachment.mime_type || '').startsWith('video/'));
  // Si el medio (audio/video) aún se está transcodificando, mostramos un estado
  // discreto en lugar del player para no cargar un formato no final.
  const transcoding = (isAudio || isVideo) && isTranscoding(attachment);
  // Si la transcodificación falló, igual mostramos el player (intento con el
  // original) + un botón de descarga como respaldo; no bloqueamos.
  const transcodeFailed = (isAudio || isVideo) && attachment.transcode_status === 'failed';
  const [imgUrl, setImgUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Cargar la miniatura de imágenes vía blob autenticado.
  useEffect(() => {
    let revoke = null;
    if (isImage) {
      api.chat.downloadAttachment(attachment.id)
        .then((r) => {
          revoke = URL.createObjectURL(r.data);
          setImgUrl(revoke);
        })
        .catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [attachment.id, isImage]);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const r = await api.chat.downloadAttachment(attachment.id);
      downloadBlob(r.data, attachment.file_name);
    } catch { /* el padre muestra toasts globales */ } finally { setBusy(false); }
  };

  if (isAudio) {
    if (transcoding) return <ProcessingBubble kind="audio" />;
    // key cambia con transcode_status para forzar remontaje (y recarga del src
    // compatible) cuando el adjunto pasa a 'done'.
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <AudioPlayer
          key={`aud-${attachment.id}-${attachment.transcode_status || 'na'}`}
          attachment={attachment}
        />
        {transcodeFailed && (
          <Tooltip title="Descargar (respaldo)">
            <span>
              <IconButton size="small" onClick={handleDownload} disabled={busy}>
                {busy ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    );
  }

  if (isVideo) {
    if (transcoding) return <ProcessingBubble kind="video" />;
    // Burbuja de nota de video con poster opcional + modal a pantalla completa.
    // Usa la URL autenticada por ?token= directamente en <video> (sin blob).
    const src = api.chat.attachmentStreamUrl(attachment.id);
    const isNote = attachment.attachment_kind === 'video_note';
    return (
      <>
        <Box
          onClick={() => setOpen(true)}
          sx={{
            mt: 0.5, position: 'relative', cursor: 'pointer', borderRadius: isNote ? '50%' : 2,
            overflow: 'hidden', width: isNote ? 180 : 260, height: isNote ? 180 : 'auto',
            maxWidth: 280, border: '1px solid', borderColor: 'divider', bgcolor: '#000',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <video
            key={`vid-${attachment.id}-${attachment.transcode_status || 'na'}`}
            src={src}
            poster={attachment.thumbnail_url || undefined}
            muted
            preload="metadata"
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.15)' }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlayIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
          </Box>
        </Box>
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
          <Box sx={{ position: 'relative', bgcolor: '#000' }}>
            <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
              <CloseIcon />
            </IconButton>
            <Tooltip title="Descargar">
              <IconButton onClick={handleDownload} sx={{ position: 'absolute', top: 8, right: 56, zIndex: 2, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <video key={`vidf-${attachment.id}-${attachment.transcode_status || 'na'}`}
              src={src} controls autoPlay playsInline
              style={{ width: '100%', maxHeight: '85vh', display: 'block', background: '#000' }} />
          </Box>
        </Dialog>
        {transcodeFailed && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
            <Tooltip title="Descargar (respaldo)">
              <span>
                <IconButton size="small" onClick={handleDownload} disabled={busy}>
                  {busy ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">Descargar</Typography>
          </Stack>
        )}
      </>
    );
  }

  if (isImage) {
    return (
      <>
        <Box onClick={() => imgUrl && setOpen(true)}
          sx={{ cursor: imgUrl ? 'pointer' : 'default', mt: 0.5, maxWidth: 280, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100', minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {imgUrl
            ? <img src={imgUrl} alt={attachment.file_name} style={{ maxWidth: '100%', maxHeight: 220, display: 'block' }} />
            : <CircularProgress size={20} sx={{ m: 2 }} />}
        </Box>
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
          <Box sx={{ position: 'relative', bgcolor: '#000' }}>
            <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
              <CloseIcon />
            </IconButton>
            <Tooltip title="Descargar">
              <IconButton onClick={handleDownload} sx={{ position: 'absolute', top: 8, right: 56, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {imgUrl && <img src={imgUrl} alt={attachment.file_name} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />}
          </Box>
        </Dialog>
      </>
    );
  }

  const Ico = iconForMime(attachment.mime_type, attachment.file_name);
  return (
    <Stack direction="row" spacing={1} alignItems="center"
      sx={{ mt: 0.5, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', maxWidth: 320 }}>
      <Ico color="primary" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{attachment.file_name}</Typography>
        <Typography variant="caption" color="text.secondary">{fmtSize(attachment.file_size)}</Typography>
      </Box>
      <Tooltip title="Descargar archivo">
        <span>
          <IconButton size="small" onClick={handleDownload} disabled={busy}>
            {busy ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default AttachmentItem;
